import React, { useState, useEffect, useRef } from 'react'
import { Route, Switch, Redirect, useRouteMatch, useHistory } from 'react-router-dom'
import axios from 'axios'
import mixpanel from '../../utilities/mixpanel'

import Stepper from '../ui/stepper'
import Login from './Auth/Login'
import Register from './Auth/Register'
import LTSInformation from './LTSInformation/LTSInformation'
import StudentRegistrationWizard from './AddStudents/StudentRegistrationWizard'
import StudentsListView from './Students/StudentsListView'
import SelectKlasses from './SelectKlasses/SelectKlasses'
import Checkout from './Checkout/Checkout'
import PaymentConfirmation from './PaymentConfirmation/PaymentConfirmation'
import ExitRegistrationModal from './ExitRegistrationModal'
import LoadingPage from '../leevo_ui/LoadingPage/LoadingPage'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card'
import { UserPlus, Users, GraduationCap, CreditCard, X } from 'lucide-react'
import { requestCreator } from '../../utilities/requests'
import useFacility from '../../utilities/hooks/useFacility'
import useAuth from '../../utilities/hooks/useAuth'
import DemoNotice from '../DemoNotice/DemoNotice'
import { get as _get } from 'lodash'

const { get: getStudents, cancel: cancelStudentsRequest } = requestCreator()
const { post: createCheckoutSession, cancel: cancelCheckoutSessionRequest } = requestCreator()

function RegistrationFeature() {
  const {
    path,
    url,
    params: { facility_id: facilityId }
  } = useRouteMatch()
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const history = useHistory()
  const { isSignedIn, currentUser, isLoading: isAuthLoading, signInUser, registerUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const hasInitialAuthCompleted = useRef(false)
  const [authMode, setAuthMode] = useState('login')
  const [currentStep, setCurrentStep] = useState(1)
  const [lineItems, setLineItems] = useState(null)
  const [couponCodeAmount, setCouponCodeAmount] = useState(null)
  const [couponCode, setCouponCode] = useState('')
  const [disclosures, setDisclosures] = useState([])
  const [couponCodePromotionAmount, setCouponCodePromotionAmount] = useState(null)
  const [couponCodeError, setCouponCodeError] = useState('')
  const [checkoutReady, setCheckoutReady] = useState(false)
  const checkoutVersion = useRef(0)
  const [total, setTotal] = useState(null)
  const [students, setStudents] = useState([])
  const [checkoutError, setCheckoutError] = useState('')
  const [showExitModal, setShowExitModal] = useState(false)

  const requiresLtsInfo = facility?.curriculums?.some((c) => c.requiresLtsNumber && c.sport === 'skating')

  const steps = [
    { id: 'account', label: 'Account', icon: UserPlus, route: '' },
    { id: 'students', label: 'Students', icon: Users, route: '/students' },
    { id: 'classes', label: 'Classes', icon: GraduationCap, route: '/classes' },
    { id: 'checkout', label: 'Checkout', icon: CreditCard, route: '/checkout' }
  ]

  function handleStepClick(stepNumber, stepId) {
    const step = steps.find((s) => s.id === stepId)
    if (step && stepNumber <= currentStep) {
      setCurrentStep(stepNumber)
      history.push(`${url}${step.route}`)
    }
  }

  function getHomePagePath() {
    const facilitiesUsers = currentUser && Array.isArray(currentUser.facilitiesUsers) ? currentUser.facilitiesUsers : []
    const coachFacilitiesUser = facilitiesUsers.find((user) => user.admin || user.coach)

    if (coachFacilitiesUser) return `/facilities/${coachFacilitiesUser.facility.id}/calendar`

    const isParent = !!_get(currentUser, 'students.length')
    if (isParent) return `/guardians/${currentUser.id}/students`
    return '/'
  }

  function handleExitRegistration() {
    setShowExitModal(false)
    const homePath = getHomePagePath()
    history.push(homePath)
  }

  useEffect(() => {
    if (!isAuthLoading) {
      hasInitialAuthCompleted.current = true
    }
  }, [isAuthLoading])

  useEffect(() => {
    if (isFacilityLoading || (!hasInitialAuthCompleted.current && isAuthLoading)) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isFacilityLoading, isAuthLoading])

  useEffect(() => {
    if (isSignedIn && currentUser) {
      setCurrentStep(2)
      loadStudents()
    }
  }, [isSignedIn, currentUser])

  useEffect(() => {
    if (students.length > 0 && history.location.pathname.includes('/checkout') && !lineItems) {
      updateCheckoutSession()
    }
  }, [history.location.pathname])

  function loadStudents() {
    setIsLoading(true)
    getStudents(`/api/guardians/${currentUser.id}/students`, {
      params: { include: 'current_skill_level' }
    })
      .then((studentsList) => {
        if (studentsList && studentsList.length > 0) {
          setStudents(studentsList)
        }
        setIsLoading(false)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error loading students:', error)
        }
        setIsLoading(false)
      })

    return cancelStudentsRequest
  }

  function updateCheckoutSession() {
    checkoutVersion.current += 1
    setCheckoutReady(false)
    setCouponCode('')
    setCouponCodeError('')
    setIsLoading(true)
    createCheckoutSession(`/api/facilities/${facilityId}/checkouts/new`, {
      students,
      include: 'disclosures'
    })
      .then((checkoutData) => {
        setLineItems(checkoutData.lineItems)
        setDisclosures(checkoutData.disclosures)
        setCouponCodeAmount(checkoutData.couponCodeAmount)
        setCouponCodePromotionAmount(checkoutData.couponCodePromotionAmount)
        setTotal(checkoutData.total)
        setCheckoutReady(true)
        setIsLoading(false)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error creating checkout session:', error)
          setCheckoutError(
            error.response?.data?.error || 'Unable to restore checkout. Please select your classes again.'
          )
          history.replace(`${url}/classes`)
        }
        setIsLoading(false)
      })

    return cancelCheckoutSessionRequest
  }

  async function handleLogin(credentials) {
    await signInUser(credentials)
    setCurrentStep(2)
    if (requiresLtsInfo) {
      history.push(`${url}/lts-info`)
    } else {
      history.push(`${url}/students`)
    }
  }

  async function handleRegister(userData) {
    await registerUser({ ...userData, facilityId })
    setCurrentStep(2)
    if (requiresLtsInfo) {
      history.push(`${url}/lts-info`)
    } else {
      history.push(`${url}/students`)
    }
  }

  function handleStudentAdded(student) {
    setStudents([...students, student])
    setCurrentStep(2)
    history.push(`${url}/students`)
  }

  async function handleKlassesSelected(selected) {
    const updatedStudents = students.map((student) => {
      const selectedKlassPackages =
        selected[student.id] &&
        selected[student.id].map(({ id, title }) => {
          return { id, title }
        })
      return { ...student, selectedKlassPackages: selectedKlassPackages || [] }
    })

    checkoutVersion.current += 1
    setCheckoutReady(false)
    setCouponCode('')
    setCouponCodeError('')
    setCheckoutError('')
    setIsLoading(true)

    try {
      const checkoutData = await createCheckoutSession(`/api/facilities/${facilityId}/checkouts/new`, {
        students: updatedStudents,
        include: 'disclosures'
      })

      setStudents(updatedStudents)
      setLineItems(checkoutData.lineItems)
      setDisclosures(checkoutData.disclosures)
      setCouponCodeAmount(checkoutData.couponCodeAmount)
      setCouponCodePromotionAmount(checkoutData.couponCodePromotionAmount)
      setTotal(checkoutData.total)
      setCheckoutReady(true)
      setCurrentStep(4)

      history.push(`${url}/checkout`)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error creating checkout session:', error)
        setCheckoutError(error.response?.data?.error || 'Unable to start checkout. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCouponCodeChange({ couponCode: requestedCode }) {
    const version = ++checkoutVersion.current
    setCheckoutReady(false)
    setCouponCode('')
    setCouponCodeAmount(null)
    setCouponCodePromotionAmount(null)
    setCouponCodeError('')
    setTotal((lineItems || []).reduce((sum, item) => sum + item.price, 0))

    try {
      const checkoutData = await createCheckoutSession(`/api/facilities/${facilityId}/checkouts/new`, {
        students,
        couponCode: requestedCode,
        include: 'disclosures'
      })
      if (version !== checkoutVersion.current) return

      setLineItems(checkoutData.lineItems)
      setDisclosures(checkoutData.disclosures)
      setCouponCodeAmount(checkoutData.couponCodeAmount)
      setCouponCodePromotionAmount(checkoutData.couponCodePromotionAmount)
      setTotal(checkoutData.total)
      const validCode = checkoutData.couponCodePromotionAmount != null
      setCouponCode(validCode ? requestedCode : '')
      setCouponCodeError(requestedCode && !validCode ? 'Code is not valid. No coupon has been applied.' : '')
      setCheckoutReady(true)
    } catch (error) {
      if (version === checkoutVersion.current && !axios.isCancel(error)) {
        setCouponCodeError('Unable to verify the coupon. Apply it again or remove it before continuing.')
      }
    }
  }

  useEffect(
    () => () => {
      checkoutVersion.current += 1
      cancelCheckoutSessionRequest()
    },
    []
  )

  function handleCompletedCheckout() {
    setIsLoading(true)
    getStudents(`/api/guardians/${currentUser.id}/students`)
      .then((studentsList) => {
        setStudents(studentsList)
        setIsLoading(false)
        mixpanel.track('Completed Registration')
        history.push(`${url}/payment_confirmation`)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error after checkout:', error)
        }
        setIsLoading(false)
      })
  }

  if (isLoading) {
    return <LoadingPage />
  }

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <DemoNotice />
      {isSignedIn && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Stepper steps={steps} currentStep={currentStep} onStepClick={handleStepClick} />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowExitModal(true)}
                className="text-gray-600 hover:text-gray-900 ml-4"
              >
                <X className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Exit</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <ExitRegistrationModal
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={handleExitRegistration}
      />

      <Switch>
        <Route path={`${path}/payment_confirmation`}>
          {lineItems && total !== null ? (
            <PaymentConfirmation
              lineItems={lineItems}
              total={total}
              couponCodeAmount={couponCodeAmount}
              couponCodePromotionAmount={couponCodePromotionAmount}
            />
          ) : (
            <LoadingPage />
          )}
        </Route>

        <Route path={`${path}/checkout`}>
          {!isSignedIn ? (
            <Redirect to={`${url}`} />
          ) : students.length === 0 ? (
            <Redirect to={`${url}/students`} />
          ) : !lineItems || lineItems.length === 0 || total === null ? (
            <Redirect to={`${url}/classes`} />
          ) : (
            <Checkout
              couponCode={couponCode}
              couponCodeAmount={couponCodeAmount}
              couponCodePromotionAmount={couponCodePromotionAmount}
              onCouponCodeChange={handleCouponCodeChange}
              couponCodeError={couponCodeError}
              checkoutReady={checkoutReady}
              disclosures={disclosures}
              lineItems={lineItems}
              total={total}
              students={students}
              demo={facility.demo}
              onCompletedCheckout={handleCompletedCheckout}
            />
          )}
        </Route>

        <Route path={`${path}/classes`}>
          {isSignedIn && students.length > 0 ? (
            <SelectKlasses
              studentsList={students}
              onKlassesSelected={handleKlassesSelected}
              checkoutError={checkoutError}
            />
          ) : (
            <LoadingPage />
          )}
        </Route>

        <Route path={`${path}/students/add`}>
          {isSignedIn ? (
            <StudentRegistrationWizard
              onStudentAdded={handleStudentAdded}
              onCancel={() => {
                history.push(`${url}/students`)
              }}
            />
          ) : (
            <LoadingPage />
          )}
        </Route>

        <Route path={`${path}/students`}>
          {isSignedIn ? (
            <StudentsListView
              students={students}
              facilityId={facilityId}
              onAddStudent={() => history.push(`${url}/students/add`)}
              onContinue={() => {
                setCheckoutError('')
                setCurrentStep(3)
                history.push(`${url}/classes`)
              }}
              onStudentUpdated={loadStudents}
            />
          ) : (
            <LoadingPage />
          )}
        </Route>

        <Route path={`${path}/lts-info`}>{isSignedIn ? <LTSInformation /> : <LoadingPage />}</Route>

        <Route path={`${path}/register`}>
          {isSignedIn ? (
            <Redirect to={`${url}/students`} />
          ) : (
            <Register
              onSubmit={handleRegister}
              onToggleMode={() => history.push(`${url}/login`)}
              facilityName={facility?.name}
              demo={facility.demo}
            />
          )}
        </Route>

        <Route path={`${path}/login`}>
          {isSignedIn ? (
            <Redirect to={`${url}/students`} />
          ) : (
            <Login onSubmit={handleLogin} onToggleMode={() => setAuthMode('register')} facilityName={facility?.name} />
          )}
        </Route>

        <Route path={`${path}`}>
          {!isSignedIn ? (
            authMode === 'login' ? (
              <Login
                onSubmit={handleLogin}
                onToggleMode={() => {
                  setAuthMode('register')
                  history.push(`${url}/register`)
                }}
                facilityName={facility?.name}
              />
            ) : (
              <Register
                onSubmit={handleRegister}
                onToggleMode={() => {
                  setAuthMode('login')
                  history.push(`${url}/login`)
                }}
                demo={facility.demo}
                facilityName={facility?.name}
              />
            )
          ) : (
            <div className="max-w-4xl mx-auto px-4 py-8">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome back, {currentUser?.preferredName || currentUser?.fullName}!</CardTitle>
                  <CardDescription>Let&apos;s get started with your registration at {facility?.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {requiresLtsInfo && (
                      <Button
                        onClick={() => history.push(`${url}/lts-info`)}
                        variant="outline"
                        className="w-full justify-start h-auto p-4"
                      >
                        <div className="text-left">
                          <div className="font-semibold">Learn About LTS Membership</div>
                          <div className="text-sm text-gray-600">Important information before registration</div>
                        </div>
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setCurrentStep(2)
                        history.push(`${url}/students`)
                      }}
                      className="w-full justify-start h-auto p-4 bg-purple-600 hover:bg-purple-700"
                    >
                      <Users className="w-6 h-6 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold">Manage Students</div>
                        <div className="text-sm">
                          {students.length > 0 ? `${students.length} student(s) added` : 'Add your first student'}
                        </div>
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </Route>
      </Switch>
    </div>
  )
}

export default RegistrationFeature
